import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Delivery = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newZone, setNewZone] = useState('');
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDeliveries();
    fetchZones();
  }, []);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/deliveries', {
        params: { status: filter !== 'all' ? filter : undefined }
      });
      setDeliveries(response.data || []);
    } catch (error) {
      console.error('Erreur chargement livraisons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const response = await api.get('/admin/delivery-zones');
      setZones(response.data || []);
    } catch (error) {
      console.error('Erreur chargement zones:', error);
    }
  };

  const updateDeliveryStatus = async (id, status, driverName = '', estimatedTime = '') => {
    try {
      await api.put(`/admin/deliveries/${id}/status`, { status, driverName, estimatedTime });
      fetchDeliveries();
    } catch (error) {
      console.error('Erreur mise à jour livraison:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const addZone = async () => {
    if (!newZone) return;
    try {
      await api.post('/admin/delivery-zones', { postalCode: newZone, city: '' });
      setNewZone('');
      setShowZoneModal(false);
      fetchZones();
    } catch (error) {
      console.error('Erreur ajout zone:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  const removeZone = async (postalCode) => {
    if (!window.confirm(`Supprimer la zone ${postalCode} ?`)) return;
    try {
      await api.delete(`/admin/delivery-zones/${postalCode}`);
      fetchZones();
    } catch (error) {
      console.error('Erreur suppression zone:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-600' },
      in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-600' },
      delivered: { label: 'Livrée', color: 'bg-green-100 text-green-600' },
      failed: { label: 'Échec', color: 'bg-red-100 text-red-600' }
    };
    const b = badges[status] || badges.pending;
    return <span className={`px-2 py-1 rounded-full text-xs ${b.color}`}>{b.label}</span>;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      delivered: 'Livrée',
      failed: 'Échec'
    };
    return labels[status] || status;
  };

  const stats = {
    total: deliveries.length,
    pending: deliveries.filter(d => d.status === 'pending').length,
    inProgress: deliveries.filter(d => d.status === 'in_progress').length,
    delivered: deliveries.filter(d => d.status === 'delivered').length,
    failed: deliveries.filter(d => d.status === 'failed').length
  };

  const filteredDeliveries = deliveries.filter(d => {
    const matchFilter = filter === 'all' || d.status === filter;
    const matchSearch = d.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        d.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchFilter && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestion des livraisons</h1>
          <p className="text-gray-500 mt-1">Suivez et gérez toutes les livraisons</p>
        </div>
        <button 
          onClick={() => setShowZoneModal(true)} 
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Gérer les zones
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-md p-4 text-center">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-md p-4 text-center border-l-4 border-yellow-500">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-xs">En attente</p>
        </div>
        <div className="bg-blue-50 rounded-xl shadow-md p-4 text-center border-l-4 border-blue-500">
          <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
          <p className="text-xs">En cours</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-md p-4 text-center border-l-4 border-green-500">
          <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
          <p className="text-xs">Livrées</p>
        </div>
        <div className="bg-red-50 rounded-xl shadow-md p-4 text-center border-l-4 border-red-500">
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          <p className="text-xs">Échec</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg transition ${filter === 'all' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Toutes</button>
            <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-lg transition ${filter === 'pending' ? 'bg-primary text-white' : 'bg-gray-100'}`}>En attente</button>
            <button onClick={() => setFilter('in_progress')} className={`px-4 py-2 rounded-lg transition ${filter === 'in_progress' ? 'bg-primary text-white' : 'bg-gray-100'}`}>En cours</button>
            <button onClick={() => setFilter('delivered')} className={`px-4 py-2 rounded-lg transition ${filter === 'delivered' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Livrées</button>
            <button onClick={() => setFilter('failed')} className={`px-4 py-2 rounded-lg transition ${filter === 'failed' ? 'bg-primary text-white' : 'bg-gray-100'}`}>Échec</button>
          </div>
          <div className="relative">
            <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border rounded-lg w-64" />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Liste des livraisons */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">N° Commande</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Adresse</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Livreur</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliveries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Aucune livraison trouvée
                  </td>
                </tr>
              ) : (
                filteredDeliveries.map(delivery => (
                  <tr key={delivery.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm font-bold">{delivery.order_number}</td>
                    <td className="px-4 py-3">{delivery.customer_name}</td>
                    <td className="px-4 py-3 text-sm">{delivery.delivery_address}</td>
                    <td className="px-4 py-3">{delivery.driver_name || '-'}</td>
                    <td className="px-4 py-3">{getStatusBadge(delivery.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setSelectedDelivery(delivery)} className="text-secondary hover:underline text-sm">Détails</button>
                        <select 
                          value={delivery.status} 
                          onChange={(e) => updateDeliveryStatus(delivery.id, e.target.value)} 
                          className="px-2 py-1 border rounded text-sm"
                        >
                          <option value="pending">En attente</option>
                          <option value="in_progress">En cours</option>
                          <option value="delivered">Livrée</option>
                          <option value="failed">Échec</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Détails livraison */}
      {selectedDelivery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Détails livraison #{selectedDelivery.order_number}</h2>
              <button onClick={() => setSelectedDelivery(null)} className="text-gray-500 text-2xl">✕</button>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Client</p>
                <p>{selectedDelivery.customer_name}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Adresse</p>
                <p>{selectedDelivery.delivery_address}</p>
                <p>{selectedDelivery.delivery_city} - {selectedDelivery.delivery_postal}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Statut</p>
                {getStatusBadge(selectedDelivery.status)}
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <button className="flex-1 bg-primary text-white py-2 rounded-lg">Suivre en direct</button>
              <button onClick={() => setSelectedDelivery(null)} className="flex-1 bg-gray-200 py-2 rounded-lg">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestion zones */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Zones de livraison</h2>
            <p className="text-sm text-gray-500 mb-4">Codes postaux desservis (Haut-Rhin)</p>
            
            <div className="flex gap-2 mb-4">
              <input 
                type="text" 
                placeholder="Code postal (ex: 68100)" 
                value={newZone} 
                onChange={(e) => setNewZone(e.target.value)} 
                className="flex-1 px-3 py-2 border rounded-lg" 
                maxLength="5" 
              />
              <button 
                onClick={addZone} 
                className="bg-primary text-white px-4 py-2 rounded-lg"
              >
                Ajouter
              </button>
            </div>
            
            <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
              {zones.map(zone => (
                <span key={zone.id} className="bg-gray-100 px-3 py-1 rounded-full flex items-center gap-2">
                  {zone.postal_code}
                  <button 
                    onClick={() => removeZone(zone.postal_code)} 
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            
            <div className="mt-4 flex gap-3">
              <button 
                onClick={() => setShowZoneModal(false)} 
                className="flex-1 bg-primary text-white py-2 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delivery;